/**
 * Screen 1 — DISABLED (enrollment landing). Shown when the project has
 * `auto_campaign_enabled=false`. Includes the "what this does" pitch, the
 * Enable CTA, and a collapsed accordion of advanced config knobs.
 */
import { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { Project } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Section } from '@/components/ui/section';
import { Spinner } from '@/components/spinner';
import { summarizeApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  ConfigForm,
  toOverridePayload,
  type ConfigFormValues,
} from './config-form';
import {
  useEnableAutoCampaign,
  usePatchAutoCampaignConfig,
} from '@/features/auto-campaign/mutations';

const CHECKLIST = [
  { label: 'Site audit', hint: 'only when >15 days stale' },
  { label: 'Keyword refresh', hint: 'only when >15 days stale' },
  { label: 'Competitor scan' },
  { label: '3 content briefs', hint: 'each with PDF' },
  { label: '9 off-page variants', hint: 'LinkedIn/Medium/Dev.to, each PDF' },
  { label: 'Rank tracking snapshot' },
];

export interface EnrollmentCardProps {
  project: Project;
}

export function EnrollmentCard({ project }: EnrollmentCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [configDraft, setConfigDraft] = useState<ConfigFormValues | null>(null);
  const enable = useEnableAutoCampaign(project.id);
  const patchConfig = usePatchAutoCampaignConfig(project.id);

  async function handleEnable(): Promise<void> {
    try {
      // If the user tweaked config while the accordion was open, persist those
      // overrides FIRST so they're already in place by the time the first fire
      // runs. We don't block enrollment on config errors — the enable side is
      // the load-bearing action.
      if (configDraft) {
        try {
          await patchConfig.mutateAsync(
            toOverridePayload(configDraft, false, false),
          );
        } catch (e) {
          const s = summarizeApiError(e, {
            fallbackTitle: 'Could not save custom config',
          });
          toast.warning(s.title, {
            description: `${s.description} — enabling with defaults instead.`,
          });
        }
      }
      await enable.mutateAsync();
      toast.success('Auto-pilot enabled', {
        description: 'Next: link a Telegram chat to receive digests.',
      });
    } catch (e) {
      const s = summarizeApiError(e, {
        fallbackTitle: 'Could not enable auto-pilot',
      });
      toast.error(s.title, { description: s.description });
    }
  }

  return (
    <Section>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Status: Disabled
            </div>
            <h2 className="mt-1 text-xl font-semibold tracking-tight">
              Auto-pilot for {project.name}
            </h2>
          </div>
          <Sparkles className="h-6 w-6 text-primary" aria-hidden />
        </div>

        <p className="text-sm text-muted-foreground">
          Set this up once and get automated SEO output every{' '}
          <span className="font-medium text-foreground">Mon &amp; Thu at 11:00 IST</span>
          {' '}— delivered to Telegram.
        </p>

        <div className="rounded-lg border border-border/60 bg-card/40 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Each fire runs
          </div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {CHECKLIST.map((c) => (
              <li key={c.label} className="flex items-baseline gap-2">
                <span className="text-emerald-500">✓</span>
                <span className="font-medium">{c.label}</span>
                {c.hint ? (
                  <span className="text-xs text-muted-foreground">
                    ({c.hint})
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-sm text-muted-foreground">
          You review the digest, post the drafts. That's it.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="lg"
            onClick={handleEnable}
            disabled={enable.isPending || patchConfig.isPending}
          >
            {enable.isPending ? <Spinner className="mr-2" /> : null}
            Enable auto-pilot
          </Button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            aria-expanded={expanded}
          >
            Advanced
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                expanded && 'rotate-180',
              )}
            />
          </button>
        </div>

        {expanded ? (
          <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 p-4">
            <ConfigForm
              onChange={setConfigDraft}
              showForceFullRun={false}
              showSchedule={false}
            />
            <p className="mt-3 text-xs text-muted-foreground">
              These overrides will be saved when you click "Enable auto-pilot".
              You can always change them later from Settings.
            </p>
          </div>
        ) : null}
      </div>
    </Section>
  );
}
